import React, { useState, useEffect, useMemo, useRef } from 'react';

import { wktToGeoJSON } from '@terraformer/wkt';
import { Modal, Control, openConfirmPopup } from '@vlabs/uikit';
import cn from 'classnames';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

import { RegionsEdit } from '@vlabs/monokit/features/regions-edit';
import { HLSMedia } from '@vlabs/monokit/shared/ui/hls-media';

import { FIGURES } from '../constants';
import { convertEditDataToFormValues } from '../convertEditDataToFormValues';
import type { TDroiFormat } from '../convertEditDataToFormValues';
import { arrayCoordsToObject } from '../coordsConverters';
import st from './RegionsEditModal.module.sass';
import type {
  TRotationDeg,
  TPolygonCoords,
  TMultiPolygonCoords,
  TRectangle,
  TPolygon,
  TMediaSize,
  TEditData,
  TRetransmission,
} from '../types';

interface RegionsEditModalProps {
  isOpen?: boolean;
  onClose?: VoidFunction;
  roi?: TRectangle;
  droi?: TPolygon | TRectangle;
  /** Формат работы с DROI: 'polygon' — WKT (легаси), 'rect' — прямоугольные зоны (point3). */
  droiFormat?: TDroiFormat;
  /** Заголовок модалки; по умолчанию — перевод 'roi.modal.title'. */
  title?: React.ReactNode;
  initialRotationDeg?: TRotationDeg;
  retransmission?: TRetransmission;
  handleEditData?: (data: TEditData) => void;
}

export function RegionsEditModal({
  isOpen = false,
  onClose = undefined,
  roi = undefined,
  droi = undefined,
  droiFormat = 'polygon',
  title = undefined,
  retransmission = undefined,
  handleEditData = undefined,
  initialRotationDeg = 0,
}: RegionsEditModalProps): React.JSX.Element {
  const { t } = useTranslation();

  const [inputRegionsList, setInputRegionsList] = useState<(TRectangle | TPolygon)[]>([]);
  const [regionsEditData, setRegionsEditData] = useState<(TRectangle | TPolygon)[] | null>(null);
  const [mediaOriginSize, setMediaOriginSize] = useState<TMediaSize>({ width: 0, height: 0 });
  const [rotationDeg, setRotationDeg] = useState<TRotationDeg>();
  const [shouldShowRotateConfirmPopup, setShouldShowRotateConfirmPopup] = useState(true);
  // StrictMode дважды вызывает эффекты монтирования — ref-гвард делает инициализацию
  // идемпотентной (иначе входные регионы задваиваются)
  const inputInitializedRef = useRef(false);

  useEffect(() => {
    if (inputInitializedRef.current) return;
    inputInitializedRef.current = true;

    if (inputRegionsList.length) return;

    if (droiFormat === 'rect') {
      // point3: DROI — прямоугольная зона; нулевой размер не рисуем (нет зоны)
      const droiRect = droi as TRectangle | undefined;
      if (droiRect && droiRect.width > 0 && droiRect.height > 0) {
        setInputRegionsList((prev) => [...prev, { ...droiRect, type: 'droi', figure: 'rect' }]);
      }
    } else if ((droi as TPolygon | undefined)?.area) {
      const { coordinates, type: figureType } = wktToGeoJSON((droi as TPolygon).area);
      const figure = FIGURES[figureType] ?? figureType;

      setInputRegionsList(coordinates.map((regionCoords: TPolygonCoords | TMultiPolygonCoords) => {
        const coords = figureType === 'MultiPolygon' ? regionCoords[0] : regionCoords;
        /* Для корректности WKT-формата у полигона первая и последняя координата одинаковая,
        но при рисовании это не требуется, поэтому её убираем */
        if (isEqual(coords?.[0], coords?.at(-1))) coords?.pop();

        return {
          coords: arrayCoordsToObject(coords as TPolygonCoords),
          mode: 'abs',
          form: 'wkt',
          type: 'droi',
          figure,
        };
      }));
    }

    let initialRoiRegion: TRectangle;

    if (!roi) {
      initialRoiRegion = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        mode: 'abs',
        type: 'roi',
        figure: 'rect',
      };

      setInputRegionsList((prev) => [initialRoiRegion, ...prev]);
      return;
    }

    initialRoiRegion = {
      ...roi,
      type: 'roi',
      figure: 'rect',
    };

    setInputRegionsList((prev) => [initialRoiRegion, ...prev]);
  }, []);

  useEffect(() => {
    if (rotationDeg === undefined) {
      setRotationDeg(initialRotationDeg);
      return;
    }

    const onRotateMedia = () => {
      if (!mediaOriginSize.width) return;

      const nextMediaSize = {
        width: mediaOriginSize.height,
        height: mediaOriginSize.width,
      };

      setMediaOriginSize(nextMediaSize);
    };

    onRotateMedia();
  }, [initialRotationDeg, rotationDeg]);

  const onChangeRotationDeg = (degree: TRotationDeg) => {
    if (shouldShowRotateConfirmPopup) {
      openConfirmPopup({
        title: t('roi.modal.confirm_popup.rotate.title') as unknown as string,
        message: t('roi.modal.confirm_popup.rotate.message'),
        type: 'warning',
        onConfirm: () => {
          setRotationDeg(degree);
          setShouldShowRotateConfirmPopup(false);
        },
        confirmLabel: t('roi.modal.confirm_popup.rotate.confirm_button') as unknown as string,
        confirmButtonProps: { 'data-testid': 'roiModal.confirmRotateButton' },
      });

      return;
    }

    setRotationDeg(degree);
  };

  const onSave = () => {
    const formValues = convertEditDataToFormValues(
      regionsEditData,
      rotationDeg,
      droi as TPolygon | undefined,
      droiFormat,
    );
    handleEditData?.(formValues);
    onClose?.();
  };

  const onCloseModal = () => {
    const { droi: editDroi, roi: editRoi } = convertEditDataToFormValues(
      regionsEditData,
      rotationDeg,
      droi as TPolygon | undefined,
      droiFormat,
    );

    if (!isEqual(droi, editDroi) || !isEqual(roi, editRoi) || initialRotationDeg !== rotationDeg) {
      openConfirmPopup({
        title: t('roi.modal.confirm_popup.unsaved_changes.title') as unknown as string,
        message: t('roi.modal.confirm_popup.unsaved_changes.message'),
        type: 'warning',
        onConfirm: onClose,
        confirmLabel: t('roi.modal.confirm_popup.unsaved_changes.confirm_button') as unknown as string,
        confirmButtonProps: { 'data-testid': 'roiModal.confirmCloseButton' },
      }, { className: st.ConfirmPopup });

      return;
    }

    onClose?.();
  };

  const actions = (
    (
      <div className={st.Actions}>
        <Control.Button
          data-testid="roiModal.saveButton"
          kind="primary"
          onClick={onSave}
        >
          {t('btn.apply')}
        </Control.Button>

        <Control.Button
          data-testid="roiModal.closeButton"
          kind="secondary"
          onClick={onCloseModal}
          variant="outlined"
        >
          {t('btn.cancel')}
        </Control.Button>
      </div>
    )
  );

  const media = useMemo(() => (
    <HLSMedia
      onChange={setMediaOriginSize}
      rotationDeg={rotationDeg}
      src={retransmission?.url}
      token={retransmission?.token}
    />
  ), [retransmission?.url, retransmission?.token, rotationDeg]);

  return (
    <Modal
      className={cn('Modal', st.Root)}
      footer={actions}
      isOpen={isOpen}
      onRequestClose={onCloseModal}
      shouldCloseOnOverlayClick={false}
      size="small"
    >
      <div className={st.Body}>
        <h4 className={st.Title}>{title ?? t('roi.modal.title')}</h4>

        <div className={st.Content}>
          <RegionsEdit
            droiFormat={droiFormat}
            media={media}
            mediaOriginSize={mediaOriginSize}
            onChangeRotationDeg={onChangeRotationDeg}
            onUpdate={setRegionsEditData}
            regions={inputRegionsList}
            rotationDeg={rotationDeg}
          />
        </div>
      </div>
    </Modal>
  );
}
